<template>
    <div>
        <div :class="headingClasses">Subscriptions</div>
        <div :class="gridClasses">
            <div :class="gridKeyClasses">Marketing:</div>
            <div :class="gridValueClasses">
                <vx-icon-loading v-if="loadingSubscriptions && marketingStatus === 'unknown'" spin></vx-icon-loading>
                <contact-subscription-badge
                    v-else
                    type="marketing"
                    :status="marketingStatus"
                ></contact-subscription-badge>
            </div>
            <div :class="gridKeyClasses">Transactional:</div>
            <div :class="gridValueClasses">
                <vx-icon-loading
                    v-if="loadingSubscriptions && transactionalStatus === 'unknown'"
                    spin
                ></vx-icon-loading>
                <contact-subscription-badge
                    v-else
                    type="transactional"
                    :status="transactionalStatus"
                ></contact-subscription-badge>
            </div>
            <div v-if="props.editable && onCorporate() && canUnsubscribe" class="col-span-full mt-2">
                <vx-button color="muted-light" size="md" block @click="unsubscribe">
                    Unsubscribe <font-awesome-icon widthAuto :icon="faArrowRightFromBracket"></font-awesome-icon>
                </vx-button>
            </div>
            <div v-if="props.editable && !onCorporate()" class="col-span-full mt-2">
                <vx-button
                    color="muted-light"
                    data-test="remove-from-group"
                    size="md"
                    block
                    @click="removeFromCurrentGroup"
                >
                    Remove From Group <font-awesome-icon widthAuto :icon="faUserMinus"></font-awesome-icon>
                </vx-button>
            </div>
        </div>
        <div :class="headingClasses">Tags</div>
        <div class="mb-6">
            <vx-disclosure appearance="boxed" v-model:active="tagsDisclosure">
                <template v-slot:header>
                    <font-awesome-icon fixedWidth class="text-slate-300" :icon="faTag"></font-awesome-icon>
                    <vx-icon-loading v-if="loadingTags && !tags.length" spin></vx-icon-loading>
                    <span v-else>{{ tags.length }}</span>
                </template>
                <template v-slot:content>
                    <vx-alert :closeable="false" v-if="!loadingTags && !tags.length"
                        >No tags were added to this contact.</vx-alert
                    >
                    <div v-else class="flex flex-wrap gap-2">
                        <div
                            v-for="tag in tags"
                            :key="tag"
                            class="py-0.5 px-2 bg-slate-100 break-all rounded-[4px] flex items-center text-slate-500 text-xs"
                        >
                            {{ tag }}
                        </div>
                    </div>
                    <vx-button
                        v-if="props.editable"
                        color="muted-light"
                        size="md"
                        block
                        class="mt-4"
                        @click="showTagsModal = true"
                    >
                        {{ !loadingTags && !tags.length ? 'Add' : 'Edit' }}
                        <font-awesome-icon widthAuto :icon="faFilePen" />
                    </vx-button>
                </template>
            </vx-disclosure>

            <contact-tags-modal v-model:visible="showTagsModal"></contact-tags-modal>
        </div>
        <template v-if="FEATURES.contact_groups && !groupId">
            <div :class="headingClasses">Groups</div>
            <div class="mb-6">
                <vx-disclosure appearance="boxed" v-model:active="groupsDisclosure">
                    <template v-slot:header>
                        <font-awesome-icon fixedWidth class="text-slate-300" :icon="faGrid2Plus"></font-awesome-icon>
                        <vx-icon-loading v-if="loadingGroups && !groups.length" spin></vx-icon-loading>
                        <span v-else>
                            {{ groups.length }}
                        </span>
                    </template>
                    <template v-slot:content>
                        <vx-alert v-if="!loadingGroups && !groups.length" :closeable="false"
                            >No groups were specified for this contact.</vx-alert
                        >
                        <div v-else class="flex flex-col gap-3">
                            <div v-for="group in groups" :key="group.group_id">
                                <div class="flex items-center gap-2">
                                    <span class="line-clamp-4 text-sm font-extrabold text-slate-800">
                                        {{ group.group_name || '-' }}
                                    </span>
                                    <vx-badge v-if="group.is_preferred" size="xs" color="violet">
                                        Preferred <font-awesome-icon widthAuto :icon="faStar"></font-awesome-icon>
                                    </vx-badge>
                                </div>
                                <div>
                                    <vx-popover
                                        hover
                                        arrow
                                        placement="top"
                                        class="[&>div:first-child]:truncate text-sm text-slate-500 w-full"
                                    >
                                        {{ group.group_id }}
                                        <template v-slot:content>
                                            {{ group.group_id }}
                                        </template>
                                    </vx-popover>
                                </div>
                            </div>
                        </div>
                        <vx-button
                            v-if="props.editable && onCorporate()"
                            color="muted-light"
                            size="md"
                            block
                            class="mt-4"
                            @click="showGroupsModal = true"
                        >
                            {{ !loadingGroups && !groups.length ? 'Add' : 'Edit' }}
                            <font-awesome-icon widthAuto :icon="faFilePen" />
                        </vx-button>
                        <contact-groups-modal v-model:visible="showGroupsModal"></contact-groups-modal>
                    </template>
                </vx-disclosure>
            </div>
        </template>
        <div :class="headingClasses">Custom Attributes</div>
        <vx-icon-loading spin v-if="loadingCustomAttributes && !customAttributes.length"></vx-icon-loading>
        <vx-alert :closeable="false" v-else-if="!customAttributes.length" class="text-sm"
            >No custom attributes were added to this contact.</vx-alert
        >
        <div class="flex flex-col gap-2 text-sm">
            <div v-for="customAttribute in customAttributes" :key="customAttribute.key">
                <div class="break-all text-xs text-slate-500">{{ customAttribute.key }}:</div>
                <div class="break-all">
                    {{ customAttribute.value }}
                </div>
            </div>
            <vx-button
                v-if="props.editable"
                color="muted-light"
                size="md"
                block
                class="mt-4"
                @click="showCustomAttributesModal = true"
            >
                {{ !loadingCustomAttributes && !customAttributes.length ? 'Add' : 'Edit' }}
                <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
            <contact-custom-attributes-modal
                v-model:visible="showCustomAttributesModal"
            ></contact-custom-attributes-modal>
        </div>
    </div>
</template>

<script setup>
import {
    faArrowRightFromBracket,
    faFilePen,
    faGrid2Plus,
    faStar,
    faTag,
    faUserMinus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxDisclosure, VxIconLoading, VxBadge, VxPopover, VxButton, VxSwal } from '@voxie/frontend-components';
import { inject, ref, watch } from 'vue';
import ContactSubscriptionBadge from '../ContactSubscriptionBadge.vue';
import { useContactDetails } from '../useContactDetails';
import ContactTagsModal from '~/components/contacts/modals/ContactTagsModal.vue';
import ContactCustomAttributesModal from '~/components/contacts/modals/ContactCustomAttributesModal.vue';
import ContactGroupsModal from '~/components/contacts/modals/ContactGroupsModal.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';
import { throwSwalRequestError } from '~/utils/request';

const { onCorporate, getCurrentGroupId } = useCurrentGroup();

const props = defineProps({
    editable: {
        type: Boolean,
        default: false,
    },
});

const showTagsModal = ref(false);
const showGroupsModal = ref(false);
const showCustomAttributesModal = ref(false);

const FEATURES = inject('FEATURES');

const tagsDisclosure = ref(false);
const groupsDisclosure = ref(false);

const headingClasses = 'font-extrabold text-base mb-2 flex items-center justify-between';
const gridClasses =
    'grid grid-cols-[84px_1fr] items-center gap-y-2 gap-x-1 text-slate-800 text-sm mb-6 last-of-type:mb-0';
const gridKeyClasses = 'break-all text-xs text-slate-500';
const gridValueClasses = 'break-all';

const {
    groupId,

    loadingGroups,
    groups,
    deleteGroup,

    canUnsubscribe,
    unsubscribe,
    marketingStatus,
    transactionalStatus,
    loadingSubscriptions,

    loadingTags,
    tags,

    loadingCustomAttributes,
    customAttributes,
} = useContactDetails();

watch([() => groups.value.length, () => tags.value.length], () => {
    groupsDisclosure.value = groups.value.length <= 25;
    tagsDisclosure.value = tags.value.length <= 25;
});

const emit = defineEmits(['currentGroupRemoved']);

const toasted = useToasted();

const removeFromCurrentGroup = async () => {
    VxSwal.confirmation(
        'Remove From Group',
        'Are you sure? This contact will be removed from the current group. There is no undo.',
        'danger',
        () =>
            deleteGroup({ group_id: getCurrentGroupId() })
                .then(() => {
                    emit('currentGroupRemoved');
                    toasted.success('Contact removed from the current group.');
                })
                .catch(throwSwalRequestError)
    );
};
</script>
